import { Component, Input, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RecognitionVisualizationConfig } from '../bbox-gallery-card/bbox-gallery-card.component';

// ─────────────────────────────────────────────────────────────────────────────

const TAG_META: Record<string, { label: string; color: string; bg: string }> = {
  postobon:    { label: 'Postobon',    color: '#6366f1', bg: 'rgba(99,102,241,.12)'  },
  competencia: { label: 'Competencia', color: '#f43f5e', bg: 'rgba(244,63,94,.12)'   },
  precios:     { label: 'Precios',     color: '#f59e0b', bg: 'rgba(245,158,11,.12)'  },
  nevera:      { label: 'Nevera',      color: '#14b8a6', bg: 'rgba(20,184,166,.12)'  },
};

export interface ObjectConfidenceRow {
  name:      string;
  tag:       string;
  tagLabel:  string;
  tagColor:  string;
  tagBg:     string;
  count:     number;
  avgScore:  number;
  minScore:  number;
  maxScore:  number;
  highConf:  number;   // score >= 0.9
  lowConf:   number;   // score < 0.7
  scoreBarW: number;   // 0-100
  scoreColor: string;
  isOthers:  boolean;
}

type SortKey = 'count' | 'avgScore' | 'name' | 'highConf';

@Component({
  selector: 'app-confidence-table-card',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './confidence-table-card.component.html',
  styleUrl:    './confidence-table-card.component.scss',
})
export class ConfidenceTableCardComponent implements OnChanges {
  @Input() config!: RecognitionVisualizationConfig;
  @Input() loading = false;

  activeTag    = 'all';
  showOthers   = false;
  sortBy: SortKey = 'count';
  sortDir      = -1; // -1 desc, 1 asc

  tags: { key: string; label: string; color: string }[] = [];
  allRows:      ObjectConfidenceRow[] = [];
  displayRows:  ObjectConfidenceRow[] = [];

  ngOnChanges(): void { this.build(); }

  private build(): void {
    if (!this.config?.rows?.length) return;

    const allObjs = this.config.rows.flatMap(r => r.objects);

    // Tags disponibles
    const tagKeys = [...new Set(allObjs.map(o => o.tag))].filter(t => TAG_META[t]);
    this.tags = [
      { key: 'all', label: 'Todos', color: '#94a3b8' },
      ...tagKeys.map(t => ({ key: t, label: TAG_META[t].label, color: TAG_META[t].color })),
    ];

    // Agrupar por name+tag
    const map: Record<string, { tag: string; scores: number[] }> = {};
    for (const obj of allObjs) {
      const key = `${obj.name}||${obj.tag}`;
      if (!map[key]) map[key] = { tag: obj.tag, scores: [] };
      map[key].scores.push(obj.score);
    }

    this.allRows = Object.entries(map).map(([key, val]) => {
      const [name] = key.split('||');
      const scores  = val.scores;
      const avg     = scores.reduce((a, b) => a + b, 0) / scores.length;
      const meta    = TAG_META[val.tag] ?? { label: val.tag, color: '#94a3b8', bg: 'rgba(148,163,184,.1)' };
      return {
        name,
        tag:       val.tag,
        tagLabel:  meta.label,
        tagColor:  meta.color,
        tagBg:     meta.bg,
        count:     scores.length,
        avgScore:  avg,
        minScore:  Math.min(...scores),
        maxScore:  Math.max(...scores),
        highConf:  scores.filter(s => s >= 0.9).length,
        lowConf:   scores.filter(s => s < 0.7).length,
        scoreBarW: Math.round(avg * 100),
        scoreColor: avg >= 0.9 ? '#4ade80' : avg >= 0.7 ? '#fb923c' : '#f87171',
        isOthers:  name === 'Others',
      };
    });

    this.applyFilters();
  }

  applyFilters(): void {
    let rows = this.allRows;
    if (this.activeTag !== 'all') rows = rows.filter(r => r.tag === this.activeTag);
    if (!this.showOthers)         rows = rows.filter(r => !r.isOthers);

    rows = [...rows].sort((a, b) => {
      const diff = (a[this.sortBy] as number) - (b[this.sortBy] as number);
      return typeof a[this.sortBy] === 'string'
        ? (a.name.localeCompare(b.name)) * this.sortDir
        : diff * this.sortDir;
    });

    this.displayRows = rows;
  }

  setTag(key: string): void  { this.activeTag = key;  this.applyFilters(); }
  toggleOthers(): void        { this.showOthers = !this.showOthers; this.applyFilters(); }

  setSort(key: SortKey): void {
    if (this.sortBy === key) this.sortDir *= -1;
    else { this.sortBy = key; this.sortDir = -1; }
    this.applyFilters();
  }

  sortIcon(key: SortKey): string {
    if (this.sortBy !== key) return 'pi-sort';
    return this.sortDir === -1 ? 'pi-sort-amount-down' : 'pi-sort-amount-up-alt';
  }

  pct(s: number): string { return `${Math.round(s * 100)}%`; }
  trackByName(_: number, r: ObjectConfidenceRow): string { return `${r.tag}|${r.name}`; }
}
